import type { Locale as DateFnsLocale } from 'date-fns'
import { format, formatDistance, formatDistanceStrict, formatRelative, formatDuration } from 'date-fns'
import { enUS, zhCN, zhTW, zhHK, th } from 'date-fns/locale'
import { formatInTimeZone } from 'date-fns-tz'

import { LangEnum } from '@/constants/locales'

const cachedLocales: Record<string, DateFnsLocale> = {
  [LangEnum.ENG]: enUS,
  [LangEnum.CN]: zhCN,
  [LangEnum.TW]: zhTW,
  [LangEnum.HK]: zhHK,
  [LangEnum.THAI]: th,
}
const fallbackLocale = cachedLocales[LangEnum.ENG]

const getDateFnsLocale: (locale: string) => DateFnsLocale = (locale: string) => {
  try {
    const localeModule = cachedLocales[locale]

    if (!localeModule) {
      throw new Error('not found')
    }

    return localeModule
  } catch {
    console.warn(`[useI18nDateFns] locale "${locale}" not found, use fallback locale "en" instead`)
    return fallbackLocale
  }
}

type DateFnsFn<T extends any[] = any[]> = (...args: T) => any
type WrapperFunctionType = <T extends DateFnsFn>(
  fn: T,
  locale: Locale,
  optionsIndex?: number
) => (...args: T extends DateFnsFn<infer U> ? U : any[]) => ReturnType<T>

const generateWrapperFunction: WrapperFunctionType =
  (fn, locale, optionsIndex = 2) =>
  (...args) => {
    if (!args[optionsIndex]) args[optionsIndex] = {}
    if (!args[optionsIndex].locale) args[optionsIndex].locale = locale
    args[0] = args[0].toString() === 'Invalid Date' ? new Date() : args[0]
    return fn(...args)
  }

export function useI18nDateFns() {
  const { locale: i18nLocale } = useI18n()
  const dateFnsLocale = getDateFnsLocale(i18nLocale.value)
  return {
    dateFnsLocale,
    format: generateWrapperFunction(format, dateFnsLocale),
    formatDistance: generateWrapperFunction(formatDistance, dateFnsLocale),
    formatDistanceStrict: generateWrapperFunction(formatDistanceStrict, dateFnsLocale),
    formatRelative: generateWrapperFunction(formatRelative, dateFnsLocale),
    formatDuration: generateWrapperFunction(formatDuration, dateFnsLocale, 1),
    formatInTimeZone: generateWrapperFunction(formatInTimeZone, dateFnsLocale, 3),
  }
}
